import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, HandHeart, BellRing, Wrench, ArchiveX } from "lucide-react";
import Logo from "@/components/Logo";
import { inspectActionToken, consumeActionToken } from "@/lib/api";

/**
 * Public page that customers land on when they tap a button in the Monday Brief email.
 * Shows a confirmation, executes the action against the API, and offers a link to
 * the full brief.
 */

const ACTION_META = {
  handle: {
    icon: Wrench,
    verb: "mark this one 'I'll handle it'",
    successTitle: "Got it — we've marked this one yours.",
    successBody: "You're in the driver's seat. Come back to your brief when it's done and we'll close the loop.",
    cta: "I'll handle it",
    tone: "primary",
  },
  help: {
    icon: HandHeart,
    verb: "ask us to handle this",
    successTitle: "We're on it.",
    successBody: "One of our advisors will reach out today.",
    cta: "Yes, please handle it",
    tone: "accent",
  },
  remind: {
    icon: BellRing,
    verb: "remind you about this next Monday",
    successTitle: "Set aside for next Monday.",
    successBody: "We'll bring this back into your brief with next week's read.",
    cta: "Remind me next Monday",
    tone: "primary",
  },
  dismiss: {
    icon: ArchiveX,
    verb: "hide this from future briefs",
    successTitle: "Hidden.",
    successBody: "We'll only bring this back if it becomes critical.",
    cta: "Not interested",
    tone: "muted",
  },
};

export default function BriefAction() {
  const [params] = useSearchParams();
  const token = params.get("t");

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Missing action link. Please open the brief email again.");
      return;
    }
    inspectActionToken(token)
      .then(setInfo)
      .catch((err) => {
        setError(err?.response?.data?.detail || "This link is no longer valid.");
      });
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      await consumeActionToken({ token });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "We couldn't complete that action.");
    } finally {
      setBusy(false);
    }
  };

  const meta = info ? ACTION_META[info.action] : null;
  const Icon = meta?.icon || CheckCircle2;
  const btnBg = meta?.tone === "accent" ? "var(--ta-secondary)"
              : meta?.tone === "muted"  ? "var(--ta-surface-alt)"
              :                           "var(--ta-primary)";
  const btnColor = meta?.tone === "muted" ? "var(--ta-primary)" : "#fff";

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="brief-action-page">
      <header className="w-full max-w-screen-md mx-auto px-6 h-16 flex items-center">
        <Logo />
      </header>

      <main className="max-w-lg mx-auto px-6 pt-8 page-fade">
        {error && !done && (
          <div data-testid="brief-action-error">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
              style={{ background: "#FDECEA", color: "#B04635" }}>
              <XCircle size={26} />
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tight" style={{ color: "var(--ta-primary)" }}>
              We couldn't open that link.
            </h1>
            <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>{error}</p>
            <Link to="/brief" className="mt-6 inline-flex items-center justify-center h-12 px-6 rounded-2xl text-base font-medium text-white"
              style={{ background: "var(--ta-primary)" }} data-testid="brief-action-open-brief">
              Open my Monday Brief
            </Link>
          </div>
        )}

        {!info && !error && (
          <div className="flex items-center gap-2" style={{ color: "var(--ta-text-secondary)" }}>
            <Loader2 size={18} className="animate-spin" /> Checking your link…
          </div>
        )}

        {info && !done && !error && (
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
              Quick confirmation
            </div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]"
              style={{ color: "var(--ta-primary)" }} data-testid="brief-action-headline">
              Ready to {meta?.verb}?
            </h1>
            <div className="mt-6 p-5 rounded-2xl bg-white border" style={{ borderColor: "var(--ta-border)" }}>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
                {info.recommendation.tag}
              </div>
              <div className="font-display text-lg font-semibold mt-1" style={{ color: "var(--ta-primary)" }}>
                {info.recommendation.title}
              </div>
            </div>

            <button onClick={confirm} disabled={busy} data-testid="brief-action-confirm"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 min-h-[60px] rounded-2xl text-lg font-semibold disabled:opacity-50"
              style={{ background: btnBg, color: btnColor }}>
              {busy ? <Loader2 size={20} className="animate-spin" /> : (<><Icon size={20}/> {meta?.cta}</>)}
            </button>

            <Link to="/brief" className="mt-4 inline-block text-sm font-medium" style={{ color: "var(--ta-text-secondary)" }}>
              Or open my full Monday Brief
            </Link>
          </div>
        )}

        {done && info && (
          <div data-testid="brief-action-done">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
              style={{ background: "var(--ta-success-bg)", color: "var(--ta-success)" }}>
              <CheckCircle2 size={26} />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]"
              style={{ color: "var(--ta-primary)" }}>
              {meta.successTitle}
            </h1>
            <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
              {meta.successBody}
            </p>
            <Link to="/brief" className="mt-8 inline-flex items-center justify-center h-12 px-6 rounded-2xl text-base font-medium text-white"
              style={{ background: "var(--ta-primary)" }} data-testid="brief-action-open-brief">
              Open my full Monday Brief
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
